// ClientFinancialHub.tsx
import React, { useState, useCallback, useEffect, useImperativeHandle } from 'react';
import { Form, InputNumber, Select, Tabs, Layout, Card, Steps, Divider, Input, List, Tag, Statistic, Table, Space, Row, Col, Typography, Button, Modal, App } from 'antd';
import { keepPreviousData, useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { API_BASE_URL } from '../../constants/config';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { useUserPanel } from '../../contexts/UserPanelContext';
import { useOutletContext } from 'react-router-dom';
import {
  UserOutlined,
  SearchOutlined,
  TransactionOutlined,
  HistoryOutlined,
  DollarOutlined,
  ArrowLeftOutlined,
  EditOutlined,
  LinkOutlined,
  IdcardOutlined,
  ArrowUpOutlined,
  CopyOutlined,
  ArrowDownOutlined,
  InfoCircleOutlined,
  SelectOutlined,
  ProfileOutlined,
  StopOutlined
} from '@ant-design/icons';
// Importar componentes existentes
import { gamificationApi } from '../../shared/api/gamificationApi';
import { HistorySection } from './HistorySection';
import { AccountHistoryTimeline } from './AccountHistoryTimeline';
import { UserModals } from './UserModals';
import PanelSelector from '../../shared/components/PanelSelector';
import { CreateUserModal } from '../../components/CreateUserModal';
import { usePanelsStore } from '../../shared/stores/usePanelsStore';
import { getAndSanitize } from '../../shared/utils/sanitization';
import { useEffectiveAdminId, useAdminSelector } from '../../contexts/AdminSelectorContext';

const { Text, Title } = Typography;
const { Header } = Layout;

// Interfaces
interface UserRecord {
  id: string;
  username: string;
  currentBalance: number;
  userId?: string;
  accountName?: string;
  password?: string;
  createdAt: string;
  historyData?: any[];
  userNumber?: string;
  code?: string;
  accountHistory?: any[];
  admin?: {
    id: string;
    username: string;
    role: string;
  };
}

interface Operation {
  id: string;
  type: 'DEPOSIT' | 'WITHDRAW';
  userId: string;
  adminId: string;
  amount: number | null;
  bonus: number;
  receipt?: string;
  status: string;
  createdAt: Date;
  user: {
    id: string;
    username: string;
    accountName?: string;
  };
  linkedTransferId: number | null;
  executionTime: number;
}

interface ActionModalState {
  type: 'deposit' | 'withdraw' | 'balance' | null;
  userData?: UserRecord;
}

interface PaginatedResponse {
  success: boolean;
  data: UserRecord[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

interface OperationsResponse {
  data: Operation[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

interface CheckOperationParams {
  username: string;
  panelId: string;
}

interface EditFormProps {
  operation: Operation;
  onCancel: () => void;
  onSubmit: (values: { amount: number; bonus: number }) => Promise<void>;
}

// Componente de edición de operación
const EditOperationForm: React.FC<EditFormProps> = ({ operation, onCancel, onSubmit }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  
  useEffect(() => {
    if (operation) {
      form.setFieldsValue({
        amount: operation.amount || 0,
        bonus: operation.bonus || 0
      });
    }
  }, [operation, form]);
  
  return (
    <Form form={form} layout="vertical" onFinish={onSubmit}>
      <Form.Item
        name="amount"
        label={t('common.amount')}
        rules={[{ required: true, message: t('operations.inputAmount') }]}
      >
        <InputNumber
          style={{ width: '100%' }}
          formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
          parser={(value: string | undefined): number => {
            const parsed = parseFloat((value || '').replace(/\$\s?|(,*)/g, ''));
            return isNaN(parsed) ? 0 : parsed;
          }}
          min={0}
          precision={2}
        />
      </Form.Item>

      <Form.Item
        name="bonus"
        label={t('common.bonus')}
        rules={[{ required: true, message: t('operations.inputBonus') }]}
      >
        <InputNumber
          style={{ width: '100%' }}
          formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
          parser={(value: string | undefined): number => {
            const parsed = parseFloat((value || '').replace(/\$\s?|(,*)/g, ''));
            return isNaN(parsed) ? 0 : parsed;
          }}
          min={0}
          precision={2}
        />
      </Form.Item>

      <Form.Item>
        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
          <Button onClick={onCancel}>
            {t('common.cancel')}
          </Button>
          <Button type="primary" htmlType="submit">
            {t('operations.updateOperation')}
          </Button>
        </Space>
      </Form.Item>
    </Form>
  );
};

export interface UsersTableRef {
  openCreateUserModal: () => void;
}

// Componente principal
export const UsersTable: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const token = getAndSanitize('token');
  const headers = { Authorization: `Bearer ${token}` };
  const { message: contextMessage, modal: contextModal } = App.useApp();

  // Get effective admin ID for filtering (respects SuperAdmin admin selector)
  const effectiveAdminId = useEffectiveAdminId();
  const { isSuperAdmin } = useAdminSelector();

  // Get ref from outlet context to expose create user function
  const context = useOutletContext<{ usersTableRef?: React.RefObject<UsersTableRef> }>();
  const contextRef = context?.usersTableRef;

  // Estados - Usuarios
  const [searchText, setSearchText] = useState<string>('');
  const [debouncedSearchText, setDebouncedSearchText] = useState<string>('');
  const [selectedUser, setSelectedUser] = useState<UserRecord | null>(null);
  const [currentUserPage, setCurrentUserPage] = useState(1);
  const [userPageSize, setUserPageSize] = useState(10);
  const [actionModalOpen, setActionModalOpen] = useState<ActionModalState>({ type: null });
  const [isCheckingBalance, setIsCheckingBalance] = useState(false);
  const [isCheckingHistory, setIsCheckingHistory] = useState(false);
  const [createUserModalOpen, setCreateUserModalOpen] = useState(false);
  const [cancellingRollover, setCancellingRollover] = useState(false);
  const [cancellingAntiLavado, setCancellingAntiLavado] = useState(false);

  const { checkBalance, checkHistory } = useUserPanel();

  // Expose create user function through context ref
  useImperativeHandle(contextRef, () => ({
    openCreateUserModal: () => {
      setCreateUserModalOpen(true);
    },
  }), []);


  // Estados para el selector de panel
  const [availablePanels, setAvailablePanels] = useState<Array<{ id: string; name?: string; isActive?: boolean }>>([]);
  const [panelSelectorOpen, setPanelSelectorOpen] = useState(false);
  const [isLoadingPanels, setIsLoadingPanels] = useState(false);
  const [pendingOperation, setPendingOperation] = useState<{
    type: 'balance' | 'history' | null;
    userData: UserRecord | null;
  }>({ type: null, userData: null });
  
  // Estados para operaciones
  const [operationsSearchParams, setOperationsSearchParams] = useState({
    username: '',
    type: undefined as 'DEPOSIT' | 'WITHDRAW' | undefined,
    linkedTransferId: ''
  });
  const [operationsPagination, setOperationsPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [selectedOperation, setSelectedOperation] = useState<Operation | null>(null);
  const [isEditModalVisible, setIsEditModalVisible] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>('history');
  
  // Sincronizar búsqueda entre ambas secciones
  useEffect(() => {
    if (selectedUser) {
      setOperationsSearchParams(prev => ({
        ...prev,
        username: selectedUser.username
      }));
    }
  }, [selectedUser]);
  
  // Debounce para búsqueda
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchText(searchText);
      setCurrentUserPage(1);
    }, 300);
    
    return () => clearTimeout(handler);
  }, [searchText]);
  
  // Consulta de rollover activo del usuario seleccionado
  const { data: activeRollover, refetch: refetchRollover } = useQuery({
    queryKey: ['activeRollover', selectedUser?.id],
    queryFn: () => gamificationApi.getUserActiveRollover(selectedUser!.id),
    enabled: !!selectedUser,
  });

  // Consulta de usuarios
  const { data: usersResponse, isLoading: isLoadingUsers } = useQuery({
    queryKey: ['panel-users', currentUserPage, userPageSize, debouncedSearchText, effectiveAdminId],
    queryFn: async () => {
      const params: Record<string, string | number> = {
        page: currentUserPage,
        limit: userPageSize
      };

      // Only include adminId if it's not null and not undefined
      if (effectiveAdminId !== null && effectiveAdminId !== undefined) {
        params.adminId = effectiveAdminId;
      }

      if (debouncedSearchText) {
        if (/^\d+$/.test(debouncedSearchText)) {
          params.userId = debouncedSearchText;
        } else {
          params.username = debouncedSearchText;
        }
      }


      const requestHeaders = {
        ...headers,
        // Only include x-admin-id header if not null and not undefined
        ...(effectiveAdminId !== null && effectiveAdminId !== undefined && { 'x-admin-id': effectiveAdminId })
      };

      const response = await axios.get<PaginatedResponse>(
        `${API_BASE_URL}/api/panel/users`,
        { headers: requestHeaders, params }
      );
      return response.data;
    },
    staleTime: 30000
  });
  
// Utility functions to calculate totals from operations
// Utility functions to calculate totals from operations with debugging
const calculateUserTotals = (operations: Operation[], username: string): { totalDeposits: number; totalWithdraws: number } => {
  
  // Ensure we're filtering correctly by checking nested user property
  const userOperations = operations.filter(op => op.user?.username === username);
  
  // Calculate totals, handling nulls safely
  const totalDeposits = userOperations
    .filter(op => op.type === 'DEPOSIT')
    .reduce((sum, op) => sum + (Number(op.amount) || 0), 0);
    
  const totalWithdraws = userOperations
    .filter(op => op.type === 'WITHDRAW')
    .reduce((sum, op) => sum + (Number(op.amount) || 0), 0);
  
  return { totalDeposits, totalWithdraws };
};
  
  // Consulta de operaciones
const { data: operationsData, isLoading: isLoadingOperations } = useQuery({
  queryKey: [
    'operations', 
    operationsPagination.current, 
    operationsPagination.pageSize, 
    operationsSearchParams.username, 
    operationsSearchParams.type, 
    operationsSearchParams.linkedTransferId
  ],
  queryFn: async () => {
    const token = getAndSanitize('token');
    
    // Extraer adminId del token JWT
    const decodedToken = token ? JSON.parse(atob(token.split('.')[1])) : null;
    const adminId = decodedToken?.userId;
    
    if (!adminId) {
      console.error('No adminId found in token');
      return {
        data: [],
        total: 0,
        page: operationsPagination.current,
        limit: operationsPagination.pageSize,
        hasMore: false
      };
    }
    
    const params: Record<string, string | number | undefined> = {
      page: operationsPagination.current,
      limit: 1000, // Get more operations to calculate totals accurately
      sortBy: 'createdAt',
      sortOrder: 'desc',
      adminId: adminId,
    };

    if (operationsSearchParams.username) params.username = operationsSearchParams.username;
    if (operationsSearchParams.type) params.type = operationsSearchParams.type;
    if (operationsSearchParams.linkedTransferId) params.linkedTransferId = operationsSearchParams.linkedTransferId;

    const response = await axios.get<OperationsResponse>(`${API_BASE_URL}/api/operations`, {
      headers: { Authorization: `Bearer ${token}` },
      params
    });
    
    return response.data;
  },
  // Make sure operations load regardless of tab to have data for stats
  enabled: true,
  placeholderData: keepPreviousData,
  staleTime: 30000,
});

  
  // Mutación para actualizar operaciones
  const updateOperationMutation = useMutation({
    mutationFn: (variables: { id: string; data: { amount: number; bonus: number } }) =>
      axios.patch(
        `${API_BASE_URL}/api/operations/${variables.id}`,
        variables.data,
        {
          headers: { Authorization: `Bearer ${getAndSanitize('token')}` }
        }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['operations'] });
      contextMessage.success(t('operations.operationUpdated'));
      setIsEditModalVisible(false);
      setSelectedOperation(null);
    },
    onError: (error: any) => {
      contextMessage.error(error.response?.data?.message || 'Failed to update operation');
    }
  });
  
  // Handlers para usuarios
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  }, []);
  
  const handleBalanceCheck = useCallback(
    async (record: UserRecord) => {
      try {
        // Obtener paneles disponibles
        setIsLoadingPanels(true);
const panels = await usePanelsStore.getState().fetchPanels();

        setAvailablePanels(panels);
        setIsLoadingPanels(false);
        
        if (!panels || panels.length === 0) {
          contextMessage.error("No hay paneles disponibles para realizar esta operación");
          return;
        }
        
        // Si hay un solo panel, usarlo directamente
        if (panels.length === 1) {
          setIsCheckingBalance(true);
          try {
            await checkBalance({ 
              username: record.username,
              panelId: panels[0].id
            } as CheckOperationParams);
            
            // Actualizar UI
            setTimeout(() => {
              queryClient.invalidateQueries({ queryKey: ['panel-users', currentUserPage, userPageSize, debouncedSearchText] });
              
              if (selectedUser && selectedUser.username === record.username) {
                const cachedData = queryClient.getQueryData<PaginatedResponse>(['panel-users', currentUserPage, userPageSize, debouncedSearchText]);
                if (cachedData && cachedData.data) {
                  const updatedUser = cachedData.data.find(user => user.id === selectedUser.id);
                  if (updatedUser) {
                    setSelectedUser({...updatedUser});
                  }
                }
              }
            }, 300);
            
            if (selectedUser) {
              setTimeout(async () => {
                try {
                  const directResponse = await axios.get<{ success: boolean, data: UserRecord[] }>(
                    `${API_BASE_URL}/api/panel/users`,
                    { 
                      headers,
                      params: { username: record.username } 
                    }
                  );
                  
                  if (directResponse.data.success && directResponse.data.data.length > 0) {
                    const latestUserData = directResponse.data.data[0];
                    setSelectedUser(prev => {
                      if (prev && prev.id === latestUserData.id) {
                        return { ...prev, currentBalance: latestUserData.currentBalance };
                      }
                      return prev;
                    });
                  }
                } catch (error) {
                  console.error('Error en consulta directa:', error);
                }
              }, 600);
            }
            
            contextMessage.success(t("users.userTableComponent.messages.balanceUpdated"));
          } catch (error) {
            console.error('Error al actualizar balance:', error);
            contextMessage.error(t("users.userTableComponent.messages.balanceError"));
          } finally {
            setIsCheckingBalance(false);
          }
          return;
        }
        
        // Si hay varios paneles, mostrar selector
        setPendingOperation({
          type: 'balance',
          userData: record
        });
        setPanelSelectorOpen(true);
      } catch (error) {
        contextMessage.error(t("users.userTableComponent.messages.balanceError"));
        console.error('Error al actualizar balance:', error);
        setIsLoadingPanels(false);
      }
    },
    [checkBalance, t, selectedUser, currentUserPage, userPageSize, debouncedSearchText, queryClient, headers, contextMessage]
  );

  const handleHistoryCheck = useCallback(
    async (record: UserRecord) => {
      try {
        // Obtener paneles disponibles
        setIsLoadingPanels(true);
const panels = await usePanelsStore.getState().fetchPanels();
        setAvailablePanels(panels);
        setIsLoadingPanels(false);
        
        if (!panels || panels.length === 0) {
          contextMessage.error("No hay paneles disponibles para realizar esta operación");
          return;
        }
        
        // Si hay un solo panel, usarlo directamente
        if (panels.length === 1) {
          setIsCheckingHistory(true);
          try {
            await checkHistory({ 
              username: record.username,
              panelId: panels[0].id
            } as CheckOperationParams);
            
            // Actualizar UI
            setTimeout(() => {
              queryClient.invalidateQueries({ queryKey: ['panel-users', currentUserPage, userPageSize, debouncedSearchText] });
              
              if (selectedUser && selectedUser.username === record.username) {
                const cachedData = queryClient.getQueryData<PaginatedResponse>(['panel-users', currentUserPage, userPageSize, debouncedSearchText]);
                if (cachedData && cachedData.data) {
                  const updatedUser = cachedData.data.find(user => user.id === selectedUser.id);
                  if (updatedUser) {
                    setSelectedUser({...updatedUser});
                  }
                }
              }
            }, 300);
            
            if (selectedUser) {
              setTimeout(async () => {
                try {
                  const directResponse = await axios.get<{ success: boolean, data: UserRecord[] }>(
                    `${API_BASE_URL}/api/panel/users`,
                    { 
                      headers,
                      params: { username: record.username } 
                    }
                  );
                  
                  if (directResponse.data.success && directResponse.data.data.length > 0) {
                    const latestUserData = directResponse.data.data[0];
                    setSelectedUser(prev => {
                      if (prev && prev.id === latestUserData.id) {
                        return { ...prev, historyData: latestUserData.historyData };
                      }
                      return prev;
                    });
                  }
                } catch (error) {
                  console.error('Error en consulta directa:', error);
                }
              }, 600);
            }
            
            contextMessage.success(t("users.userTableComponent.messages.historyUpdated"));
          } catch (error) {
            console.error('Error al actualizar historial:', error);
            contextMessage.error(t("users.userTableComponent.messages.historyError"));
          } finally {
            setIsCheckingHistory(false);
          }
          return;
        }
        
        // Si hay varios paneles, mostrar selector
        setPendingOperation({
          type: 'history',
          userData: record
        });
        setPanelSelectorOpen(true);
      } catch (error) {
        contextMessage.error(t("users.userTableComponent.messages.historyError"));
        console.error('Error al actualizar historial:', error);
        setIsLoadingPanels(false);
      }
    },
    [checkHistory, t, selectedUser, currentUserPage, userPageSize, debouncedSearchText, queryClient, headers, contextMessage]
  );

  const handlePanelSelect = useCallback(async (panelId: string) => {
    // Cerrar el selector de panel
    setPanelSelectorOpen(false);
    
    // Verificar si hay una operación pendiente
    if (!pendingOperation.type || !pendingOperation.userData) return;
    
    const record = pendingOperation.userData;
    
    if (pendingOperation.type === 'balance') {
      setIsCheckingBalance(true);
      try {
        await checkBalance({ 
          username: record.username,
          panelId
        } as CheckOperationParams);
        
        // Actualizar UI
        setTimeout(() => {
          queryClient.invalidateQueries({ queryKey: ['panel-users', currentUserPage, userPageSize, debouncedSearchText] });
          
          if (selectedUser && selectedUser.username === record.username) {
            const cachedData = queryClient.getQueryData<PaginatedResponse>(['panel-users', currentUserPage, userPageSize, debouncedSearchText]);
            if (cachedData && cachedData.data) {
              const updatedUser = cachedData.data.find(user => user.id === selectedUser.id);
              if (updatedUser) {
                setSelectedUser({...updatedUser});
              }
            }
          }
        }, 300);
        
        if (selectedUser) {
          setTimeout(async () => {
            try {
              const directResponse = await axios.get<{ success: boolean, data: UserRecord[] }>(
                `${API_BASE_URL}/api/panel/users`,
                { 
                  headers,
                  params: { username: record.username } 
                }
              );
              
              if (directResponse.data.success && directResponse.data.data.length > 0) {
                const latestUserData = directResponse.data.data[0];
                setSelectedUser(prev => {
                  if (prev && prev.id === latestUserData.id) {
                    return { ...prev, currentBalance: latestUserData.currentBalance };
                  }
                  return prev;
                });
              }
            } catch (error) {
              console.error('Error en consulta directa:', error);
            }
          }, 600);
        }
        
        contextMessage.success(t("users.userTableComponent.messages.balanceUpdated"));
      } catch (error) {
        console.error('Error al actualizar balance con panel seleccionado:', error);
        contextMessage.error(t("users.userTableComponent.messages.balanceError"));
      } finally {
        setIsCheckingBalance(false);
      }
    } else if (pendingOperation.type === 'history') {
      setIsCheckingHistory(true);
      try {
        await checkHistory({ 
          username: record.username,
          panelId 
        } as CheckOperationParams);
        
        // Actualizar UI
        setTimeout(() => {
          queryClient.invalidateQueries({ queryKey: ['panel-users', currentUserPage, userPageSize, debouncedSearchText] });
          
          if (selectedUser && selectedUser.username === record.username) {
            const cachedData = queryClient.getQueryData<PaginatedResponse>(['panel-users', currentUserPage, userPageSize, debouncedSearchText]);
            if (cachedData && cachedData.data) {
              const updatedUser = cachedData.data.find(user => user.id === selectedUser.id);
              if (updatedUser) {
                setSelectedUser({...updatedUser});
              }
            }
          }
        }, 300);
        
        if (selectedUser) {
          setTimeout(async () => {
            try {
              const directResponse = await axios.get<{ success: boolean, data: UserRecord[] }>(
                `${API_BASE_URL}/api/panel/users`,
                { 
                  headers,
                  params: { username: record.username } 
                }
              );
              
              if (directResponse.data.success && directResponse.data.data.length > 0) {
                const latestUserData = directResponse.data.data[0];
                setSelectedUser(prev => {
                  if (prev && prev.id === latestUserData.id) {
                    return { ...prev, historyData: latestUserData.historyData };
                  }
                  return prev;
                });
              }
            } catch (error) {
              console.error('Error en consulta directa:', error);
            }
          }, 600);
        }
        
        contextMessage.success(t("users.userTableComponent.messages.historyUpdated"));
      } catch (error) {
        console.error('Error al actualizar historial con panel seleccionado:', error);
        contextMessage.error(t("users.userTableComponent.messages.historyError"));
      } finally {
        setIsCheckingHistory(false);
      }
    }
    
    // Resetear la operación pendiente
    setPendingOperation({ type: null, userData: null });
  }, [selectedUser, checkBalance, checkHistory, currentUserPage, userPageSize, debouncedSearchText, queryClient, headers, contextMessage, t]);

const handleCopyCredentials = useCallback(
  async (username: string, password?: string) => {
    try {
      const token = getAndSanitize('token');
      let format = "Usuario: {username}\nClave: {password}";
      
      // Extract adminId from JWT token
      let adminId: string | null = null;
      if (token) {
        try {
          const decodedToken = JSON.parse(atob(token.split('.')[1]));
          adminId = decodedToken?.userId;
        } catch (decodeError) {
          console.warn('Error decoding token for adminId:', decodeError);
        }
      }
      
      // Get settings with adminId parameter
      if (adminId) {
        try {
          const settingsResponse = await axios.get(
            `${API_BASE_URL}/api/settings`,
            { 
              headers: { Authorization: `Bearer ${token}` },
              params: { adminId }
            }
          );
          
          if (settingsResponse?.data?.success && settingsResponse?.data?.data?.credentialFormat) {
            format = settingsResponse.data.data.credentialFormat;
          } else if (settingsResponse?.data?.credentialFormat) {
            format = settingsResponse.data.credentialFormat;
          }
        } catch (settingsError) {
          console.warn('Error getting credential format from settings, using default:', settingsError);
        }
      }
      
      if (password) {
        // Handle both {username}/{password} placeholders and literal username/password text
        const formattedCredential = format
          // Handle proper placeholders first
          .replace(/{username}/g, username)
          .replace(/{password}/g, password)
          // Handle literal text as fallback
          .replace(/\busername\b/g, username)
          .replace(/\bpassword\b/g, password)
          // Convert escaped newlines to actual newlines
          .replace(/\\\\n/g, '\n')
          .replace(/\\n/g, '\n');
          
        await navigator.clipboard.writeText(formattedCredential);
        contextMessage.success(t("users.userTableComponent.messages.credentialsCopied"));
        return;
      }
      
      // If we don't have the password, request it from the server
      const response = await axios.post(
        `${API_BASE_URL}/api/panel/user/credentials`,
        { username },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        const formattedCredential = format
          // Handle proper placeholders first
          .replace(/{username}/g, response.data.data.username)
          .replace(/{password}/g, response.data.data.password)
          // Handle literal text as fallback
          .replace(/\busername\b/g, response.data.data.username)
          .replace(/\bpassword\b/g, response.data.data.password)
          // Convert escaped newlines to actual newlines
          .replace(/\\\\n/g, '\n')
          .replace(/\\n/g, '\n');
          
        await navigator.clipboard.writeText(formattedCredential);
        contextMessage.success(t("users.userTableComponent.messages.credentialsCopied"));
      }
    } catch (error) {
      console.error("Error copying credentials:", error);
      contextMessage.error(t("users.userTableComponent.messages.copyError"));
    }
  },
  [t, contextMessage]
);
  const handleCopyText = useCallback((text: string, type = "texto") => {
    try {
      navigator.clipboard.writeText(text);
      contextMessage.success(`${type} copiado al portapapeles`);
    } catch (error) {
      console.error(`Error al copiar ${type}:`, error);
      contextMessage.error(`Error al copiar ${type}`);
    }
  }, [contextMessage]);
  
  const handleCancelRolloverRequirement = useCallback(() => {
    if (!activeRollover?.id) return;
    contextModal.confirm({
      title: 'Cancelar Rollover',
      content: 'Se cancelará el requisito de rollover (apuestas sobre el bono). El usuario ya no necesitará cumplir esta condición para retirar. ¿Continuar?',
      okText: 'Cancelar Rollover',
      okButtonProps: { danger: true },
      cancelText: 'Volver',
      onOk: async () => {
        setCancellingRollover(true);
        try {
          await gamificationApi.cancelRolloverRequirement(activeRollover.id);
          contextMessage.success('Requisito de rollover cancelado exitosamente');
          refetchRollover();
        } catch (error: any) {
          contextMessage.error(error.response?.data?.message || 'Error al cancelar rollover');
        } finally {
          setCancellingRollover(false);
        }
      },
    });
  }, [activeRollover, contextMessage, contextModal, refetchRollover]);

  const handleCancelAntiLavadoRequirement = useCallback(() => {
    if (!activeRollover?.id) return;
    contextModal.confirm({
      title: 'Cancelar Condición de Retiro',
      content: 'Se cancelará la condición de retiro (antilavado, apuestas sobre el depósito). El usuario ya no necesitará cumplir esta condición para retirar. ¿Continuar?',
      okText: 'Cancelar Cond. Retiro',
      okButtonProps: { danger: true },
      cancelText: 'Volver',
      onOk: async () => {
        setCancellingAntiLavado(true);
        try {
          await gamificationApi.cancelAntiLavadoRequirement(activeRollover.id);
          contextMessage.success('Condición de retiro cancelada exitosamente');
          refetchRollover();
        } catch (error: any) {
          contextMessage.error(error.response?.data?.message || 'Error al cancelar condición de retiro');
        } finally {
          setCancellingAntiLavado(false);
        }
      },
    });
  }, [activeRollover, contextMessage, contextModal, refetchRollover]);

  // Handlers para operaciones
  const handleOperationsSearch = useCallback((field: keyof typeof operationsSearchParams, value: any) => {
    setOperationsSearchParams(prev => ({
      ...prev,
      [field]: value
    }));
    setOperationsPagination(prev => ({
      ...prev,
      current: 1
    }));
  }, []);
  
  const handleOperationTableChange = useCallback((pagination: any) => {
    setOperationsPagination({
      current: pagination.current,
      pageSize: pagination.pageSize,
      total: pagination.total || 0
    });
  }, []);
  
  const handleEditOperation = useCallback(
    (operation: Operation) => {
      Modal.confirm({
        title: t('operations.editOperation'),
        icon: <EditOutlined />,
        content: (
          <EditOperationForm
            operation={operation}
            onCancel={() => Modal.destroyAll()}
            onSubmit={async (values) => {
              try {
                await axios.put(
                  `${API_BASE_URL}/api/panel/operation/${operation.id}`,
                  values,
                  { headers }
                );
                
                Modal.destroyAll();
                
                // Actualizar datos
                queryClient.invalidateQueries({ queryKey: ['operations', selectedUser?.username] });
                
                contextMessage.success(t('operations.operationUpdated'));
              } catch (error) {
                console.error('[EditOperation] Error updating operation:', error);
                contextMessage.error(error.response?.data?.message || 'Failed to update operation');
              }
            }}
          />
        ),
        footer: null,
        width: 500,
      });
    },
    [t, headers, queryClient, selectedUser?.username, contextMessage]
  );
  
  const handleUpdateOperation = useCallback(async (values: { amount: number; bonus: number }) => {
    if (!selectedOperation) return;
    
    await updateOperationMutation.mutateAsync({
      id: selectedOperation.id,
      data: values
    });
  }, [selectedOperation, updateOperationMutation]);
  
  // Verificar disponibilidad de paneles
  
if (isMobile) {
  return (
    <div>
      {selectedUser && (
        <Header style={{ padding: '0 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Title level={4} style={{ color: 'white', margin: 0 }}>
            <Space>
              <Button
                type="text"
                icon={<ArrowLeftOutlined />}
                onClick={() => {
                  setSelectedUser(null);
                  setActiveTab('history');
                }}
                style={{ color: 'white' }}
              />
              {selectedUser.username}
            </Space>
          </Title>
        </Header>
      )}
      
      <div style={{ padding: '0px'}}>
        {!selectedUser ? (
          // Lista de usuarios
          <Card style={{ width: '100%' }}>
            <Space direction="vertical" style={{ width: '100%' }} size={16}>
              <Title level={4} style={{ color: '#ac4949', textAlign: 'center', margin: '0 0 16px 0' }}>
                <UserOutlined style={{ marginRight: '8px' }} />
                Gestión de Usuarios
              </Title>
              
              <Input.Search
                placeholder={t("users.usersTable.searchPlaceholder") || "Search users"}
                value={searchText}
                onChange={handleSearchChange}
              />
              <List
                dataSource={usersResponse?.data || []}
                loading={isLoadingUsers}
                renderItem={(user) => (
                  <List.Item
                    onClick={() => setSelectedUser(user)}
                    style={{
                      cursor: 'pointer',
                      backgroundColor: 'transparent',
                      padding: '8px',
                      borderRadius: '4px'
                    }}
                  >
                    <Space size={4} wrap style={{ width: '100%' }}>
                      <Text strong>
                        {user.username}
                      </Text>
                      {user.accountName && (
                        <Text style={{
                          fontSize: '12px',
                          opacity: '0.8'
                        }}>
                          · {user.accountName}
                        </Text>
                      )}
                      {user.userId && (
                        <Text style={{
                          fontSize: '12px',
                          opacity: '0.8'
                        }}>
                          · {user.userId}
                        </Text>
                      )}
                      {isSuperAdmin && user.admin && (
                        <Tag color="purple" style={{ fontSize: '11px', padding: '0 6px' }}>
                          {user.admin.username}
                        </Tag>
                      )}
                    </Space>
                  </List.Item>
                )}
                pagination={{
                  current: currentUserPage,
                  pageSize: userPageSize,
                  total: usersResponse?.total || 0,
                  onChange: (page, size) => {
                    setCurrentUserPage(page);
                    if (size) setUserPageSize(size);
                  }
                }}
              />
            </Space>
          </Card>
        ) : (
          // Detalle de usuario seleccionado con tabs
          <Tabs 
            activeKey={activeTab} 
            onChange={setActiveTab}
            centered
            items={[
              {
                key: "profile",
                label: (
                  <span>
                    <UserOutlined />
                    {t('clientFinancialHub.tabs.profile') || 'Perfil'}
                  </span>
                ),
                children: (
                  <Card style={{ borderRadius: 0 }}>
                    <div style={{ padding: '0 16px 16px' }}>
                      <Space direction="vertical" style={{ width: '100%' }} size={16}>
                        <Space direction="vertical" style={{ width: '100%' }}>
                          <Space>
                            <Text type="secondary">
                              <UserOutlined /> User ID: {selectedUser.userId || 'N/A'}
                            </Text>
                            {selectedUser.userId && (
                              <Button
                                type="text"
                                icon={<CopyOutlined />}
                                onClick={() => handleCopyText(selectedUser.userId!, 'User ID')}
                                style={{ marginLeft: -8 }}
                              />
                            )}
                          </Space>
                          <Space>
                            <Text type="secondary">
                              <IdcardOutlined /> Account: {selectedUser.accountName || 'N/A'}
                            </Text>
                            {selectedUser.accountName && (
                              <Button
                                type="text"
                                icon={<CopyOutlined />}
                                onClick={() => handleCopyText(selectedUser.accountName!, 'Account')}
                                style={{ marginLeft: -8 }}
                              />
                            )}
                          </Space>
                          <Space>
                            <Text type="secondary">
                              Operation #: {selectedUser.userNumber || 'N/A'}
                            </Text>
                            {selectedUser.userNumber && (
                              <Button
                                type="text"
                                icon={<CopyOutlined />}
                                onClick={() => handleCopyText(selectedUser.userNumber!, 'Operation number')}
                              />
                            )}
                          </Space>
                          <Space>
                            <Text type="secondary">
                              <LinkOutlined /> Code: {selectedUser.code || 'N/A'}
                            </Text>
                            {selectedUser.code && (
                              <Button
                                type="text"
                                icon={<CopyOutlined />}
                                onClick={() => handleCopyText(selectedUser.code!, 'User Code')}
                                style={{ marginLeft: -8 }}
                              />
                            )}
                          </Space>
                        </Space>
                        
                        <Card 
                          style={{ 
                            background: 'linear-gradient(135deg, #ac4949 0%, #8b3333 100%)', 
                            borderRadius: '12px',
                            marginBottom: '8px',
                            boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                          }}
                        >
                          <Statistic
                            title={<Text style={{ color: 'white', fontSize: '14px' }}>Saldo Actual</Text>}
                            value={selectedUser.currentBalance || 0}
                            prefix={<DollarOutlined style={{ color: 'white' }} />}
                            valueStyle={{ color: 'white', fontSize: '24px', fontWeight: 'bold' }}
                            precision={2}
                            loading={isCheckingBalance}
                          />
                          <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'space-around' }}>
                            <Tag color="green" style={{ borderRadius: '20px', padding: '1px 10px' }}>
                              <Space size={4}>
                                <ArrowUpOutlined />$
                                {selectedUser ? calculateUserTotals(operationsData?.data || [], selectedUser.username).totalDeposits : 0}
                              </Space>
                            </Tag>
                            <Tag color="red" style={{ borderRadius: '20px', padding: '1px 10px' }}>
                              <Space size={4}>
                                <ArrowDownOutlined />$
                                {selectedUser ? calculateUserTotals(operationsData?.data || [], selectedUser.username).totalWithdraws : 0}
                              
                              </Space>
                            </Tag>
                          </div>
                        </Card>
                        
                        <Row gutter={[12, 12]}>
                          <Col span={12}>
                            <Button
                              type="primary"
                              icon={<DollarOutlined />}
                              onClick={() => handleBalanceCheck(selectedUser)}
                              loading={isCheckingBalance}
                              block
                              style={{ height: '38px' }}
                            >
                              Check Balance
                            </Button>
                          </Col>
                          <Col span={12}>
                            <Button
                              icon={<HistoryOutlined />}
                              onClick={() => handleHistoryCheck(selectedUser)}
                              loading={isCheckingHistory}
                              block
                              style={{ height: '38px' }}
                            >
                              Update History
                            </Button>
                          </Col>
                          <Col span={24}>
                            <Button
                              icon={<CopyOutlined />}
                              onClick={() => handleCopyCredentials(selectedUser.username, selectedUser.password)}
                              block
                              style={{ height: '38px' }}
                            >
                              Copy Credentials
                            </Button>
                          </Col>
                        </Row>
                        
                        <Row gutter={[12, 12]}>
                          <Col span={12}>
                            <Button
                              style={{ 
                                background: 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)', 
                                borderColor: '#389e0d',
                                height: '48px',
                                boxShadow: '0 2px 6px rgba(82,196,26,0.3)'
                              }}
                              type="primary"
                              icon={<ArrowUpOutlined />}
                              onClick={() => setActionModalOpen({ type: 'deposit', userData: selectedUser })}
                              block
                            >
                              Deposit
                            </Button>
                          </Col>
                          <Col span={12}>
                            <Button
                              style={{ 
                                background: 'linear-gradient(135deg, #ff4d4f 0%, #cf1322 100%)',
                                borderColor: '#cf1322',
                                height: '48px',
                                boxShadow: '0 2px 6px rgba(245,34,45,0.3)'
                              }}
                              type="primary"
                              icon={<ArrowDownOutlined />}
                              onClick={() => setActionModalOpen({ type: 'withdraw', userData: selectedUser })}
                              block
                            >
                              Withdraw
                            </Button>
                          </Col>
                        </Row>

                        {activeRollover?.status === 'active' && (
                          <Card size="small" title="Rollover Activo" style={{ border: '1px solid #ff4d4f' }}>
                            <Space direction="vertical" style={{ width: '100%' }} size={8}>
                              <Row justify="space-between" align="middle">
                                <Col>
                                  <Text type="secondary">Rollover (Bono)</Text>
                                </Col>
                                <Col>
                                  {activeRollover.rolloverRequired > 0 ? (
                                    <Space>
                                      <Tag color="orange">${activeRollover.rolloverRequired.toLocaleString()}</Tag>
                                      <Button
                                        danger
                                        size="small"
                                        icon={<StopOutlined />}
                                        onClick={handleCancelRolloverRequirement}
                                        loading={cancellingRollover}
                                      >
                                        Cancelar
                                      </Button>
                                    </Space>
                                  ) : (
                                    <Tag color="green">Cumplido</Tag>
                                  )}
                                </Col>
                              </Row>
                              <Row justify="space-between" align="middle">
                                <Col>
                                  <Text type="secondary">Cond. Retiro (Depósito)</Text>
                                </Col>
                                <Col>
                                  {activeRollover.antiLavadoRequired > 0 ? (
                                    <Space>
                                      <Tag color="orange">${activeRollover.antiLavadoRequired.toLocaleString()}</Tag>
                                      <Button
                                        danger
                                        size="small"
                                        icon={<StopOutlined />}
                                        onClick={handleCancelAntiLavadoRequirement}
                                        loading={cancellingAntiLavado}
                                      >
                                        Cancelar
                                      </Button>
                                    </Space>
                                  ) : (
                                    <Tag color="green">Cumplido</Tag>
                                  )}
                                </Col>
                              </Row>
                              <Row justify="space-between">
                                <Text type="secondary" style={{ fontSize: 12 }}>Depósito: ${activeRollover.depositAmount?.toLocaleString()}</Text>
                                <Text type="secondary" style={{ fontSize: 12 }}>Bono: ${activeRollover.totalBonusAmount?.toLocaleString()}</Text>
                              </Row>
                            </Space>
                          </Card>
                        )}
                      </Space>
                    </div>
                  </Card>
                )
              },
              {
                key: "history",
                label: (
                  <span>
                    <HistoryOutlined />
                    {t('clientFinancialHub.tabs.history') || 'Historial'}
                  </span>
                ),
                children: (
                  <Card style={{ borderRadius: 0, padding: 0 }}>
                    <HistorySection 
                      username={selectedUser.username} 
                      isUpdating={isCheckingHistory}
                      historyData={selectedUser.historyData}
                    />
                  </Card>
                )
              },
              {
                key: "operations",
                label: (
                  <span>
                    <TransactionOutlined />
                    {t('clientFinancialHub.tabs.operations') || 'Operaciones'}
                  </span>
                ),
                children: (
                  <Card style={{ borderRadius: 0 }}>
                    <Space direction="vertical" style={{ width: '100%' }} size={16}>
                      <Row gutter={[8, 8]}>
                        <Col span={12}>
                          <Select
                            placeholder={t('operations.filterByType')}
                            allowClear
                            value={operationsSearchParams.type}
                            onChange={(value) => handleOperationsSearch('type', value)}
                            options={[
                              { value: 'DEPOSIT', label: t('common.deposit') },
                              { value: 'WITHDRAW', label: t('common.withdraw') }
                            ]}
                            style={{ width: '100%' }}
                          />
                        </Col>
                        <Col span={12}>
                          <Input
                            placeholder={t('operations.searchByTransfer')}
                            prefix={<LinkOutlined />}
                            onChange={e => handleOperationsSearch('linkedTransferId', e.target.value)}
                            value={operationsSearchParams.linkedTransferId}
                            allowClear
                            type="number"
                            style={{ width: '100%' }}
                          />
                        </Col>
                      </Row>
                      
                      <List
                        loading={isLoadingOperations}
                        dataSource={operationsData?.data || []}
                        renderItem={(operation) => (
                          <List.Item
                            style={{ padding: '12px 0' }}
                            actions={[
                              <Button
                                type="primary"
                                icon={<EditOutlined />}
                                size="small"
                                onClick={() => handleEditOperation(operation)}
                              />
                            ]}
                          >
                            <Space direction="vertical" size={4} style={{ width: '100%' }}>
                              <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                                <Tag color={operation.type === 'DEPOSIT' ? 'green' : 'orange'}>
                                  {operation.type.toLowerCase()}
                                </Tag>
                                <Tag color="purple">${(operation.amount ?? 0).toFixed(2)}</Tag>
                              </Space>
                              <Text type="secondary" style={{ fontSize: '12px' }}>
                                {new Date(operation.createdAt).toLocaleDateString()} {new Date(operation.createdAt).toLocaleTimeString()}
                              </Text>
                              <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                                <Tag color="cyan">Bonus: ${operation.bonus.toFixed(2)}</Tag>
                                <Tag color={operation.status === 'COMPLETED' ? 'success' : 'processing'}>
                                  {operation.status.toLowerCase()}
                                </Tag>
                              </Space>
                            </Space>
                          </List.Item>
                        )}
                        pagination={{
                          current: operationsPagination.current,
                          pageSize: operationsPagination.pageSize,
                          total: operationsData?.total || 0,
                          onChange: (page, pageSize) => {
                            setOperationsPagination(prev => ({
                              ...prev,
                              current: page,
                              pageSize: pageSize || prev.pageSize
                            }));
                          }
                        }}
                      />
                    </Space>
                  </Card>
                )
              },
              {
                key: "accountHistory",
                label: (
                  <span>
                    <ProfileOutlined />
                    Historial de Cuentas
                  </span>
                ),
                children: (
                  <Card style={{ borderRadius: 0 }}>
                    <AccountHistoryTimeline
                      accountHistory={selectedUser.accountHistory || []}
                    />
                  </Card>
                )
              }
            ]}
          />
        )}
      </div>
      
      {/* Modals */}
      <UserModals
        actionModalOpen={actionModalOpen}
        setActionModalOpen={setActionModalOpen}
        currentPanelId=""
      />
      
      <CreateUserModal
        open={createUserModalOpen}
        onCancel={() => setCreateUserModalOpen(false)}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['panel-users'] });
        }}
      />
      
      <Modal
        title={t('operations.editOperation')}
        open={isEditModalVisible}
        onCancel={() => {
          setIsEditModalVisible(false);
          setSelectedOperation(null);
        }}
        footer={null}
        destroyOnClose
      >
        {selectedOperation && (
          <EditOperationForm
            operation={selectedOperation}
            onCancel={() => {
              setIsEditModalVisible(false);
              setSelectedOperation(null);
            }}
            onSubmit={handleUpdateOperation}
          />
        )}
      </Modal>
      
      <PanelSelector
        isOpen={panelSelectorOpen}
        onClose={() => setPanelSelectorOpen(false)}
        onPanelSelect={handlePanelSelect}
        panels={availablePanels}
        loading={isLoadingPanels}
        actionTitle={
          pendingOperation.type === 'balance' 
            ? 'Verificar saldo'
            : pendingOperation.type === 'history'
            ? 'Verificar historial'
            : 'Seleccionar panel'
        }
        actionDescription={
          pendingOperation.type === 'balance' 
            ? 'Selecciona el panel para verificar el saldo del usuario.'
            : pendingOperation.type === 'history'
            ? 'Selecciona el panel para verificar el historial del usuario.'
            : 'Selecciona un panel para continuar.'
        }
      />
    </div>
  );
}
  // Vista desktop con layout original
// Vista desktop con layout original
return (
  <div>
    {/* Vista desktop - mantener diseño original de dos columnas */}
    <Row gutter={[16, 16]} style={{ padding: '16px' }}>
      <Col xs={24} md={8}>
        <Card>
          <Space direction="vertical" style={{ width: '100%' }} size={16}>
            <Input.Search
              placeholder={t("users.usersTable.searchPlaceholder") || "Search users"}
              value={searchText}
              onChange={handleSearchChange}
            />
            <List
              dataSource={usersResponse?.data || []}
              loading={isLoadingUsers}
              renderItem={(user) => (
                <List.Item
                  onClick={() => setSelectedUser(user)}
                  style={{
                    cursor: 'pointer',
                    backgroundColor: selectedUser?.id === user.id ? '#ac4949' : 'transparent',
                    padding: '8px',
                    borderRadius: '4px',
                    color: selectedUser?.id === user.id ? 'white' : 'inherit'
                  }}
                >
                  <Space size={4} wrap style={{ width: '100%' }}>
                    <Text strong style={{ color: selectedUser?.id === user.id ? 'white' : 'inherit' }}>
                      {user.username}
                    </Text>
                    {user.accountName && (
                      <Text style={{
                        color: selectedUser?.id === user.id ? 'white' : 'inherit',
                        fontSize: '12px',
                        opacity: '0.8'
                      }}>
                        · {user.accountName}
                      </Text>
                    )}
                    {user.userId && (
                      <Text style={{
                        color: selectedUser?.id === user.id ? 'white' : 'inherit',
                        fontSize: '12px',
                        opacity: '0.8'
                      }}>
                        · {user.userId}
                      </Text>
                    )}
                    {isSuperAdmin && user.admin && (
                      <Tag color="purple" style={{ fontSize: '11px', padding: '0 6px' }}>
                        {user.admin.username}
                      </Tag>
                    )}
                  </Space>
                </List.Item>
              )}
              pagination={{
                current: currentUserPage,
                pageSize: userPageSize,
                total: usersResponse?.total || 0,
                onChange: (page, size) => {
                  setCurrentUserPage(page);
                  if (size) setUserPageSize(size);
                }
              }}
            />
          </Space>
        </Card>
      </Col>

      <Col xs={24} md={16}>
        {selectedUser ? (
          <Space direction="vertical" style={{ width: '100%' }} size={16}>
            <Card>
              <Space direction="vertical" style={{ width: '100%' }} size={16}>
                <Row gutter={[16, 16]}>
                  <Col span={24}>
                    <Title level={4}>{selectedUser.username}</Title>
                  </Col>
                  <Col span={12}>
                    <Space direction="vertical">
                      <Space>
                        <Text type="secondary">
                          <UserOutlined /> User ID: {selectedUser.userId || 'N/A'}
                        </Text>
                        {selectedUser.userId && (
                          <Button
                            type="text"
                            icon={<CopyOutlined />}
                            onClick={() => handleCopyText(selectedUser.userId!, 'User ID')}
                            style={{ marginLeft: -8 }}
                          />
                        )}
                      </Space>
                      <Space>
                        <Text type="secondary">
                          <IdcardOutlined /> Account: {selectedUser.accountName || 'N/A'}
                        </Text>
                        {selectedUser.accountName && (
                          <Button
                            type="text"
                            icon={<CopyOutlined />}
                            onClick={() => handleCopyText(selectedUser.accountName!, 'Account')}
                            style={{ marginLeft: -8 }}
                          />
                        )}
                      </Space>
                      <Space>
                        <Text type="secondary">
                          Operation #: {selectedUser.userNumber || 'N/A'}
                        </Text>
                        {selectedUser.userNumber && (
                          <Button
                            type="text"
                            icon={<CopyOutlined />}
                            onClick={() => handleCopyText(selectedUser.userNumber!, 'Operation number')}
                          />
                        )}
                      </Space>
                    </Space>
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="Current Balance"
                      value={selectedUser.currentBalance || 0}
                      prefix={<DollarOutlined />}
                      precision={2}
                      loading={isCheckingBalance}
                    />
                    <Space size="small" style={{ marginTop: 8 }}>
                      <Tag color="green">
                        <Space size={4}>
                          <ArrowUpOutlined />$
                          {selectedUser ? calculateUserTotals(operationsData?.data || [], selectedUser.username).totalDeposits : 0}
                        </Space>
                      </Tag>
                      <Tag color="red">
                        <Space size={4}>
                          <ArrowDownOutlined />$
                          {selectedUser ? calculateUserTotals(operationsData?.data || [], selectedUser.username).totalWithdraws : 0}
                        </Space>
                      </Tag>
                    </Space>
                  </Col>
                </Row>
                
                <Row gutter={[8, 8]}>
                  <Col span={24}>
                    <Space>
                      <Button
                        type="primary"
                        icon={<DollarOutlined />}
                        onClick={() => handleBalanceCheck(selectedUser)}
                        loading={isCheckingBalance}
                      >
                        Check Balance
                      </Button>
                      <Button
                        icon={<HistoryOutlined />}
                        onClick={() => handleHistoryCheck(selectedUser)}
                        loading={isCheckingHistory}
                      >
                        Update History
                      </Button>
                      <Button
                        icon={<CopyOutlined />}
                        onClick={() => handleCopyCredentials(selectedUser.username, selectedUser.password)}
                      >
                        Copy Credentials
                      </Button>
                      <Button
                        style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                        type="primary"
                        icon={<ArrowUpOutlined />}
                        onClick={() => setActionModalOpen({ type: 'deposit', userData: selectedUser })}
                      >
                        Deposit
                      </Button>
                      <Button
                        danger
                        type="primary"
                        icon={<ArrowDownOutlined />}
                        onClick={() => setActionModalOpen({ type: 'withdraw', userData: selectedUser })}
                      >
                        Withdraw
                      </Button>
                    </Space>
                  </Col>
                </Row>

                {activeRollover?.status === 'active' && (
                  <Card size="small" title="Rollover Activo" style={{ border: '1px solid #ff4d4f', marginTop: 8 }}>
                    <Row gutter={[16, 8]}>
                      <Col span={8}>
                        <Space direction="vertical" size={4}>
                          <Text type="secondary" style={{ fontSize: 12 }}>Rollover (Bono x{activeRollover.rolloverMultiplier})</Text>
                          {activeRollover.rolloverRequired > 0 ? (
                            <Space>
                              <Tag color="orange">${activeRollover.rolloverRequired.toLocaleString()}</Tag>
                              <Button
                                danger
                                size="small"
                                icon={<StopOutlined />}
                                onClick={handleCancelRolloverRequirement}
                                loading={cancellingRollover}
                              >
                                Cancelar
                              </Button>
                            </Space>
                          ) : (
                            <Tag color="green">Cumplido</Tag>
                          )}
                        </Space>
                      </Col>
                      <Col span={8}>
                        <Space direction="vertical" size={4}>
                          <Text type="secondary" style={{ fontSize: 12 }}>Cond. Retiro (Depósito x{activeRollover.antiLavadoMultiplier})</Text>
                          {activeRollover.antiLavadoRequired > 0 ? (
                            <Space>
                              <Tag color="orange">${activeRollover.antiLavadoRequired.toLocaleString()}</Tag>
                              <Button
                                danger
                                size="small"
                                icon={<StopOutlined />}
                                onClick={handleCancelAntiLavadoRequirement}
                                loading={cancellingAntiLavado}
                              >
                                Cancelar
                              </Button>
                            </Space>
                          ) : (
                            <Tag color="green">Cumplido</Tag>
                          )}
                        </Space>
                      </Col>
                      <Col span={8}>
                        <Space direction="vertical" size={4}>
                          <Text type="secondary" style={{ fontSize: 12 }}>Depósito: ${activeRollover.depositAmount?.toLocaleString()}</Text>
                          <Text type="secondary" style={{ fontSize: 12 }}>Bono: ${activeRollover.totalBonusAmount?.toLocaleString()}</Text>
                        </Space>
                      </Col>
                    </Row>
                  </Card>
                )}
              </Space>
            </Card>

            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              items={[
                {
                  key: "history",
                  label: (
                    <span><HistoryOutlined /> Historial del Cliente</span>
                  ),
                  children: (
                    <Card>
                      <HistorySection 
                        username={selectedUser.username} 
                        isUpdating={isCheckingHistory}
                        historyData={selectedUser.historyData}
                      />
                    </Card>
                  )
                },
                {
                  key: "operations",
                  label: (
                    <span><TransactionOutlined /> Operaciones</span>
                  ),
                  children: (
                    <Card>
                      <div style={{ marginBottom: 16 }}>
                        <Row gutter={16}>
                          <Col span={8}>
                            <Select
                              placeholder={t('operations.filterByType')}
                              allowClear
                              value={operationsSearchParams.type}
                              onChange={(value) => handleOperationsSearch('type', value)}
                              options={[
                                { value: 'DEPOSIT', label: t('common.deposit') },
                                { value: 'WITHDRAW', label: t('common.withdraw') }
                              ]}
                              style={{ width: '100%' }}
                            />
                          </Col>
                          <Col span={8}>
                            <Input
                              placeholder={t('operations.searchByTransfer')}
                              prefix={<LinkOutlined />}
                              onChange={e => handleOperationsSearch('linkedTransferId', e.target.value)}
                              value={operationsSearchParams.linkedTransferId}
                              allowClear
                              type="number"
                              style={{ width: '100%' }}
                            />
                          </Col>
                        </Row>
                      </div>
                      
                      <Table
                        dataSource={operationsData?.data || []}
                        loading={isLoadingOperations}
                        rowKey="id"
                        columns={[
                          {
                            title: t('common.type'),
                            dataIndex: 'type',
                            key: 'type',
                            render: (type: string) => (
                              <Tag color={type === 'DEPOSIT' ? 'green' : 'orange'}>
                                {type.toLowerCase()}
                              </Tag>
                            ),
                          },
                          {
                            title: t('common.amount'),
                            dataIndex: 'amount',
                            key: 'amount',
                            render: (amount: number | null) => (
                              <Tag color="purple">${(amount ?? 0).toFixed(2)}</Tag>
                            ),
                          },
                          {
                            title: t('common.bonus'),
                            dataIndex: 'bonus',
                            key: 'bonus',
                            render: (bonus: number) => (
                              <Tag color="cyan">${(bonus ?? 0).toFixed(2)}</Tag>
                            ),
                          },
                          {
                            title: t('common.status'),
                            dataIndex: 'status',
                            key: 'status',
                            render: (status: string) => (
                              <Tag color={status === 'COMPLETED' ? 'success' : 'processing'}>
                                {status.toLowerCase()}
                              </Tag>
                            ),
                          },
                          {
                            title: t('common.date'),
                            dataIndex: 'createdAt',
                            key: 'createdAt',
                            render: (date: string) => (
                              new Date(date).toLocaleDateString()
                            ),
                          },
                          {
                            title: t('common.actions'),
                            key: 'actions',
                            render: (_, record) => (
                              <Button
                                type="primary"
                                icon={<EditOutlined />}
                                size="small"
                                onClick={() => handleEditOperation(record)}
                              />
                            ),
                          },
                        ]}
                        pagination={{
                          current: operationsPagination.current,
                          pageSize: operationsPagination.pageSize,
                          total: operationsData?.total || 0,
                          onChange: (page, pageSize) => {
                            setOperationsPagination(prev => ({
                              ...prev,
                              current: page,
                              pageSize: pageSize || prev.pageSize
                            }));
                          },
                          showSizeChanger: true,
                          showTotal: (total) => `Total ${total} items`
                        }}
                        onChange={handleOperationTableChange}
                        size="middle"
                      />
                    </Card>
                  )
                },
                {
                  key: "accountHistory",
                  label: (
                    <span><ProfileOutlined /> Historial de Cuentas</span>
                  ),
                  children: (
                    <Card>
                      <AccountHistoryTimeline
                        accountHistory={selectedUser.accountHistory || []}
                      />
                    </Card>
                  )
                }
              ]}
            />
          </Space>
        ) : (
          <Card style={{ padding: '20px', textAlign: 'center' }}>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <Title level={4} style={{ color: '#ac4949' }}>
                <UserOutlined style={{ marginRight: '8px' }} />
                Gestión de Usuarios
              </Title>
              
              <Text>
                En esta sección podrás gestionar los usuarios y sus operaciones financieras.
              </Text>
              
              <Divider>
                <InfoCircleOutlined style={{ color: '#ac4949' }} />
                <Text strong style={{ marginLeft: '8px' }}>¿Cómo funciona?</Text>
              </Divider>
              
              <Steps 
                direction="vertical" 
                size="small"
                current={-1}
                style={{ maxWidth: '500px', margin: '0 auto', textAlign: 'left' }}
              >
                <Steps.Step 
                  title="Buscar usuario" 
                  description="Utiliza el buscador para encontrar usuarios por nombre o ID."
                  icon={<SearchOutlined />}
                />
                <Steps.Step 
                  title="Seleccionar usuario" 
                  description="Haz clic en un usuario de la lista para ver su información detallada."
                  icon={<SelectOutlined />}
                />
                <Steps.Step 
                  title="Gestionar finanzas" 
                  description="Consulta operaciones, historial y realiza depósitos o retiros."
                  icon={<ProfileOutlined />}
                />
              </Steps>
              
              <Text type="secondary" italic>
                Selecciona un usuario de la lista para comenzar a gestionar sus datos.
              </Text>
            </Space>
          </Card>
        )}
      </Col>
    </Row>
    
    {/* Modals */}
    <UserModals
      actionModalOpen={actionModalOpen}
      setActionModalOpen={setActionModalOpen}
      currentPanelId=""
    />
    
    <CreateUserModal
      open={createUserModalOpen}
      onCancel={() => setCreateUserModalOpen(false)}
      onSuccess={() => {
        queryClient.invalidateQueries({ queryKey: ['panel-users'] });
      }}
    />
    
    <Modal
      title={t('operations.editOperation')}
      open={isEditModalVisible}
      onCancel={() => {
        setIsEditModalVisible(false);
        setSelectedOperation(null);
      }}
      footer={null}
      destroyOnClose
    >
      {selectedOperation && (
        <EditOperationForm
          operation={selectedOperation}
          onCancel={() => {
            setIsEditModalVisible(false);
            setSelectedOperation(null);
          }}
          onSubmit={handleUpdateOperation}
        />
      )}
    </Modal>
    
    <PanelSelector
      isOpen={panelSelectorOpen}
      onClose={() => setPanelSelectorOpen(false)}
      onPanelSelect={handlePanelSelect}
      panels={availablePanels}
      loading={isLoadingPanels}
      actionTitle={
        pendingOperation.type === 'balance' 
          ? 'Verificar saldo'
          : pendingOperation.type === 'history'
          ? 'Verificar historial'
          : 'Seleccionar panel'
      }
      actionDescription={
        pendingOperation.type === 'balance' 
          ? 'Selecciona el panel para verificar el saldo del usuario.'
          : pendingOperation.type === 'history'
          ? 'Selecciona el panel para verificar el historial del usuario.'
          : 'Selecciona un panel para continuar.'
      }
    />
  </div>
);
};